// The agent draft panel's A2 layout (2026-10-23, docs/agent-draft-shadow.md,
// "The panel"; docs/decisions/2026-10-23-agent-draft-panel-a2.md). Built once
// and used in both places the agent's draft shows, so they never drift:
//   - the main AI Suggestion panel when the agent's draft is the main one
//     (SuggestionPanel.jsx, AGENT_DRAFT_PRIMARY_INTENTS, every CSR): working,
//     with the one "Process all steps and send reply" button;
//   - the admin-only agent draft column (ShadowDraftPanel.jsx): display only,
//     no Process or Send, "Preview only. Nothing here is processed or sent."
// Sections on a light grey panel, each its own white block 8 px apart with a
// small stroke icon and an 11 px uppercase label: Summary (a soft blue
// block), What's next, Steps, Reply; then the button area (CSR panel only)
// and the run's Details, collapsed. 13 px body text. Every section carries
// data-a2-section, the hook the tests and the styles read.
import { useState } from 'react'
import { Box, Button, Checkbox, CircularProgress, Collapse, Link, Stack, Tooltip, Typography } from '@mui/material'
import QuietCheckGate from './QuietCheckGate.jsx'
import { condenseNextSteps, DETAILS_LABEL, generatedAtLabel } from './agentDraftLayout.js'
import { STEP_UNCHECKED_TOOLTIP } from '@cxhub/shared'
import { placeholderSegments, PLACEHOLDER_HIGHLIGHT_BG, PLACEHOLDER_HIGHLIGHT_TEXT } from './placeholderHighlight.js'
import { NextStepText } from './NextStepTracking.jsx'

// The mockup's tokens; dark mode keeps the theme's own surfaces.
const dark = (t) => t.palette.mode === 'dark'
export const A2 = {
  panel: (t) => (dark(t) ? t.palette.background.default : '#F3F5F7'),
  section: (t) => (dark(t) ? t.palette.background.paper : '#FFFFFF'),
  text: (t) => (dark(t) ? t.palette.text.primary : '#1F2933'),
  label: (t) => (dark(t) ? t.palette.text.secondary : '#5B6673'),
  border: (t) => (dark(t) ? t.palette.divider : '#D5DBE1'),
  rule: (t) => (dark(t) ? t.palette.divider : '#E3E7EB'),
  link: (t) => (dark(t) ? t.palette.primary.main : '#1D5FD1'),
  summary: (t) => (dark(t) ? t.palette.action.hover : '#EEF3FB'),
  summaryLabel: (t) => (dark(t) ? t.palette.info.light : '#3B5A8C'),
  warnText: (t) => (dark(t) ? t.palette.warning.light : '#6B4200'),
  warnIcon: (t) => (dark(t) ? t.palette.warning.main : '#B07400'),
  primary: (t) => (dark(t) ? t.palette.primary.main : '#1D5FD1'),
  disabledBg: (t) => (dark(t) ? t.palette.action.disabledBackground : '#EEF1F4'),
  disabledBorder: (t) => (dark(t) ? t.palette.divider : '#B8C1CA'),
}
export const BODY = { fontSize: 13, lineHeight: 1.45, color: A2.text }
export const WRAP = { overflowWrap: 'anywhere', wordBreak: 'break-word' }
export const INPUT_SX = {
  '& .MuiOutlinedInput-root': { borderRadius: '6px', fontSize: 13, color: A2.text, bgcolor: A2.section },
  '& .MuiOutlinedInput-input': { py: '6px', px: '8px' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: A2.border },
}
export const TEXTAREA_SX = { ...INPUT_SX, '& .MuiOutlinedInput-root': { ...INPUT_SX['& .MuiOutlinedInput-root'], p: '8px 10px', lineHeight: 1.45 }, '& .MuiOutlinedInput-input': { p: 0 } }

// 14 px stroke icons (the mockup's).
function Icon({ children, size = 14 }) {
  return <Box component="svg" aria-hidden="true" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} sx={{ flexShrink: 0 }}>{children}</Box>
}
export const ICONS = {
  summary: <Icon><line x1="5" y1="7" x2="19" y2="7" /><line x1="5" y1="12" x2="19" y2="12" /><line x1="5" y1="17" x2="13" y2="17" /></Icon>,
  next: <Icon><polyline points="9 6 15 12 9 18" /></Icon>,
  steps: <Icon><rect x="4" y="4" width="16" height="16" rx="3" /><polyline points="8 12 11 15 16 9" /></Icon>,
  reply: <Icon><rect x="3" y="6" width="18" height="13" rx="2" /><polyline points="3 7 12 13 21 7" /></Icon>,
  warn: <Icon><circle cx="12" cy="12" r="9" /><line x1="12" y1="7" x2="12" y2="13" /><line x1="12" y1="16.5" x2="12" y2="16.6" /></Icon>,
  check: <Icon size={16}><polyline points="4 12 10 18 20 6" /></Icon>,
}

export function SectionLabel({ icon, children, right = null }) {
  return (
    <Stack direction="row" alignItems="center" spacing="6px" sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: A2.label }}>
      {icon}
      <Box component="span">{children}</Box>
      {right && <Box sx={{ flexGrow: 1 }} />}
      {right}
    </Stack>
  )
}

// One white section. section: the data-a2-section hook.
export function Section({ icon, label, testId, section, right = null, gap = '6px', children, sx = {} }) {
  return (
    <Box data-testid={testId} data-a2-section={section} sx={{ bgcolor: A2.section, px: '20px', py: '12px', display: 'flex', flexDirection: 'column', gap, minWidth: 0, ...BODY, ...WRAP, ...sx }}>
      <SectionLabel icon={icon} right={right}>{label}</SectionLabel>
      {children}
    </Box>
  )
}

export function FieldLabel({ children, htmlFor = undefined }) {
  return <Typography component="label" htmlFor={htmlFor} sx={{ fontSize: 11, color: A2.label, display: 'block' }}>{children}</Typography>
}

export function SystemChip({ system, testId = 'shadow-step-system' }) {
  if (!system) return null
  return <Box component="span" data-testid={testId} sx={{ fontSize: 11, color: A2.label, border: 1, borderColor: A2.border, borderRadius: '4px', px: '5px', lineHeight: 1.45, whiteSpace: 'nowrap' }}>{system}</Box>
}

// The grey panel the sections sit on.
export function AgentDraftShell({ testId, children, sx = {} }) {
  return (
    <Box data-testid={testId} data-a2-panel="1" sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: '8px', bgcolor: A2.panel, ...BODY, ...WRAP, ...sx }}>
      {children}
    </Box>
  )
}

// The header row: the title, its chips (the run status, "admin only" on the
// admin panel) and the action (Regenerate on the CSR panel, Hide on the
// admin one).
export function AgentDraftHeader({ title, titleTestId, chips = null, action = null, sx = {} }) {
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={0.5} data-a2-section="header" sx={{ bgcolor: A2.section, px: '20px', py: '14px', borderBottom: 1, borderColor: A2.rule, ...sx }}>
      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography fontWeight={700} sx={{ fontSize: 15, color: A2.text }} data-testid={titleTestId}>{title}</Typography>
        {chips}
      </Stack>
      {action}
    </Stack>
  )
}

// The run status chip (2026-10-26): nothing when the run is ok (the agent's
// draft shows only when its run finished, so "ok" said nothing); any other
// state in red with its name.
export function RunStatusChip({ status, testId = 'agent-draft-run-status' }) {
  if (status === 'ok') return null
  return (
    <Box component="span" data-testid={testId} data-status={status ?? 'error'} sx={{ fontSize: '0.7rem', lineHeight: '18px', height: 20, boxSizing: 'border-box', px: '8px', borderRadius: '10px', border: 1, borderColor: 'error.main', color: 'error.main' }}>
      {status || 'error'}
    </Box>
  )
}

// One small line at the very top, above the header row (2026-10-26):
// "Generated 10/26/2026 11:42 AM ET", when the draft on screen was written.
export function GeneratedAtLine({ at, testId = 'agent-draft-generated-at', sx = {} }) {
  const text = generatedAtLabel(at)
  if (!text) return null
  return <Typography data-testid={testId} sx={{ fontSize: 11, color: A2.label, lineHeight: 1.4, ...sx }}>{text}</Typography>
}

// Summary, in the soft blue block (no left border). right: on the label row,
// right-aligned (the confidence chip, 2026-10-26).
export function SummaryBlock({ summary, testId, right = null, children = null }) {
  if (!summary && !children && !right) return null
  return (
    <Box data-testid={testId} data-a2-section="summary" sx={{ bgcolor: A2.summary, px: '20px', py: '12px', display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0, ...BODY, ...WRAP }}>
      <Stack direction="row" alignItems="center" spacing="6px" data-testid={testId ? `${testId}-label-row` : undefined} sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: A2.summaryLabel }}>
        {ICONS.summary}
        <Box component="span">Summary</Box>
        {right && <Box sx={{ flexGrow: 1 }} />}
        {right && <Box sx={{ textTransform: 'none', letterSpacing: 0, fontWeight: 400, display: 'flex' }}>{right}</Box>}
      </Stack>
      {summary && <Box data-testid={testId ? `${testId}-text` : undefined} sx={{ fontSize: 13 }}>{summary}</Box>}
      {children}
    </Box>
  )
}

// What's next, condensed (agentDraftLayout.js's condenseNextSteps()): one
// numbered item per thing to do, the conditional rules as one muted line
// each. english: the item's English under it, by the step's own text.
// extra: lines shown as written after the condensed items (the own-label
// check's lines, returnTrackingSteps.js, which name a tracking number on
// purpose), each once.
// trackingLinks (2026-10-29): the suggestion's modelMeta.nextStepTrackingLinks;
// a step's tracking number links, and a package check gets the links under
// it (NextStepTracking.jsx). The muted conditions stay plain.
// lead (2026-11-04): lines shown as written before the condensed items (a
// flow's own line, the wear-and-tear check, with its link).
export function NextStepsSection({ steps = [], testId, top = null, renderEnglish = null, extra = [], lead = [], trackingLinks = null }) {
  const { items: condensed, conditions } = condenseNextSteps(steps)
  const items = []
  for (const raw of [...(lead ?? []), ...condensed]) {
    const s = String(raw ?? '').trim()
    if (s && !items.includes(s)) items.push(s)
  }
  for (const raw of extra ?? []) {
    const s = String(raw ?? '').trim()
    if (s && !items.includes(s)) items.push(s)
  }
  if (!items.length && !conditions.length && !top) return null
  return (
    <Section icon={ICONS.next} label="What's next" testId={testId} section="next">
      {top}
      {[...items.map((text) => ({ text, muted: false })), ...conditions.map((text) => ({ text, muted: true }))].map((it, i) => (
        <Box key={i} data-testid="agent-next-step" data-muted={it.muted ? '1' : '0'} sx={{ display: 'flex', gap: '8px', alignItems: 'baseline', color: it.muted ? A2.label : A2.text }}>
          <Box sx={{ fontWeight: 600, color: A2.label, width: 14, flexShrink: 0 }}>{i + 1}</Box>
          <Box data-testid="agent-next-step-text" sx={{ minWidth: 0 }}>
            {it.muted ? it.text : <NextStepText text={it.text} links={trackingLinks} />}
            {renderEnglish?.(it.text)}
          </Box>
        </Box>
      ))}
    </Section>
  )
}

export function StepsSection({ count, testId, children }) {
  return (
    <Section icon={ICONS.steps} label={`Steps · ${count}`} testId={testId} section="steps" gap="10px">
      {children}
    </Section>
  )
}

// The step's checkbox (2026-10-26): in place of the step number, with the
// number after it, smaller. Every step starts checked; an unchecked step is
// not processed. note: the tooltip when the step runs together with another
// (processAll.js's stepDependencyNote()).
export function StepCheck({ number, checked = true, onChange = null, disabled = false, note = null, testId = 'agent-draft-step-check' }) {
  const box = (
    <Checkbox size="small" checked={checked} disabled={disabled || !onChange}
      onChange={(ev) => onChange?.(ev.target.checked)}
      inputProps={{ 'aria-label': `Process step ${number}`, 'data-testid': testId }}
      sx={{ p: 0, color: A2.label, '& .MuiSvgIcon-root': { fontSize: 18 } }} />
  )
  return (
    <Stack direction="row" alignItems="center" spacing="4px" sx={{ flexShrink: 0 }}>
      {note ? <Tooltip title={note}><span style={{ display: 'inline-flex' }} data-testid={`${testId}-note`}>{box}</span></Tooltip> : box}
      <Box component="span" data-testid="agent-draft-step-number" sx={{ fontSize: 11, fontWeight: 600, color: A2.label, minWidth: 8 }}>{number}</Box>
    </Stack>
  )
}

// The step's fields under its title row, the card's full width (2026-10-26).
// Unchecked: dimmed and read only (a disabled fieldset disables every input,
// and no click reaches a select).
export function StepFields({ unchecked = false, children, testId = 'agent-draft-step-fields' }) {
  return (
    <Box component="fieldset" disabled={unchecked} data-testid={testId} data-unchecked={unchecked ? '1' : '0'}
      sx={{ border: 0, m: 0, p: 0, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '6px', ...(unchecked ? { opacity: 0.5, pointerEvents: 'none' } : {}) }}>
      {children}
    </Box>
  )
}

// A step control that is disabled while the step is unchecked, with the
// tooltip that says why.
export function UncheckedGate({ unchecked, children, title = STEP_UNCHECKED_TOOLTIP }) {
  if (!unchecked) return children
  return <Tooltip title={title}><span style={{ display: 'inline-flex' }} data-testid="agent-draft-step-unchecked-gate">{children}</span></Tooltip>
}

// One step: the title row (the checkbox and number, the title, the system
// chip, and at its right end the step's controls and its View calls link), then
// its fields at the card's full width (2026-10-26). checked / onCheck: the
// checkbox; an unchecked step stays visible, dimmed, its fields read only.
export function StepRow({ number, title, system, testId, titleTestId, systemTestId, right = null, children, dataKey, checked = true, onCheck = null, checkDisabled = false, checkNote = null, checkTestId = undefined }) {
  const unchecked = !checked
  return (
    <Box data-testid={testId} data-step-key={dataKey} data-checked={checked ? '1' : '0'} sx={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: 0 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap data-testid="agent-draft-step-title-row">
        <StepCheck number={number} checked={checked} onChange={onCheck} disabled={checkDisabled} note={checkNote} testId={checkTestId} />
        <Typography data-testid={titleTestId} sx={{ fontSize: 13, fontWeight: 600, color: A2.text, ...WRAP, ...(unchecked ? { opacity: 0.5 } : {}) }}>{title}</Typography>
        <SystemChip system={system} testId={systemTestId} />
        {right && <Box sx={{ flexGrow: 1 }} />}
        {right}
      </Stack>
      <StepFields unchecked={unchecked}>{children}</StepFields>
    </Box>
  )
}

// The Reply section; it ends right after its last line (the placeholder
// warning): it never stretches to fill the panel.
export function ReplySection({ testId, right = null, children }) {
  return (
    <Section icon={ICONS.reply} label="Reply" testId={testId} section="reply" right={right} gap="8px" sx={{ flexGrow: 0 }}>
      {children}
    </Section>
  )
}

// The one placeholder line under the reply: "1 placeholder to fill: ...".
export function PlaceholderLine({ text, testId = 'agent-draft-placeholder-line' }) {
  if (!text) return null
  return (
    <Stack direction="row" spacing="6px" alignItems="center" data-testid={testId} sx={{ fontSize: 12, color: A2.warnText }}>
      <Box sx={{ color: A2.warnIcon, display: 'flex' }}>{ICONS.warn}</Box>
      <Box>{text}</Box>
    </Stack>
  )
}

// The button area (CSR panel): the one full-width button on its own row and
// the line under it, then the other decisions in a row below.
// waiting: the quiet stale check on open runs (quietStaleCheck.js): disabled,
// no spinner, with the "Checking for updates…" tooltip.
export function PrimaryActionArea({ state, onClick, busy = false, waiting = false, testId = 'agent-draft-primary-action', children = null }) {
  const { label, line, disabled, warn } = state
  return (
    <Box data-a2-section="actions" sx={{ bgcolor: A2.section, px: '20px', py: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <QuietCheckGate waiting={waiting} block>
        <Button type="button" data-testid={testId} disabled={disabled || busy || waiting} onClick={onClick} fullWidth disableElevation
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : (disabled ? null : ICONS.check)}
          sx={{
            minHeight: 48, fontSize: 14, fontWeight: 700, textTransform: 'none', borderRadius: '8px', px: 2, py: '10px',
            color: '#FFFFFF', bgcolor: A2.primary, '&:hover': { bgcolor: A2.primary, filter: 'brightness(0.92)' },
            '&.Mui-disabled': { color: A2.label, bgcolor: A2.disabledBg, border: 1, borderStyle: 'dashed', borderColor: A2.disabledBorder },
          }}>
          {label}
        </Button>
        </QuietCheckGate>
        {line && <Typography data-testid={`${testId}-line`} data-warn={warn ? '1' : '0'} sx={{ fontSize: 12, textAlign: 'center', color: warn ? A2.warnText : A2.label }}>{line}</Typography>}
      </Box>
      {children && (
        <Box sx={{ pt: '10px', borderTop: 1, borderColor: A2.rule }}>
          {children}
        </Box>
      )}
    </Box>
  )
}

// The run's details, collapsed behind one line ("Details ▸"): the model
// calls, time, cost and model, what it read, and the tool calls. footer:
// always shown under it (the admin panel's "Preview only" line).
export function DetailsFooter({ runLine = null, sources = null, toolLines = [], footer = null, testId = 'agent-draft-details' }) {
  const [open, setOpen] = useState(false)
  const has = Boolean(runLine || sources || toolLines.length)
  if (!has && !footer) return null
  return (
    <Box data-testid={testId} data-a2-section="details" sx={{ bgcolor: A2.section, px: '20px', py: '12px', display: 'flex', flexDirection: 'column', gap: '4px', fontSize: 12, color: A2.label }}>
      {has && (
        <Link component="button" type="button" underline="hover" data-testid={`${testId}-toggle`} aria-expanded={open} onClick={() => setOpen((v) => !v)}
          sx={{ alignSelf: 'flex-start', fontSize: 12, fontWeight: 600, color: A2.label, textAlign: 'left' }}>
          {DETAILS_LABEL} {open ? '▾' : '▸'}
        </Link>
      )}
      {has && (
        <Collapse in={open} unmountOnExit>
          <Box data-testid={`${testId}-body`} sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {runLine && <Typography data-testid={`${testId}-run`} sx={{ fontSize: 12, color: A2.label }}>{runLine}</Typography>}
            {sources && <Typography data-testid="shadow-draft-sources" sx={{ fontSize: 12, color: A2.label, ...WRAP }}>{sources}</Typography>}
            {toolLines.length > 0 && (
              <Box component="ul" sx={{ m: 0, pl: 2.5, fontSize: 12, color: A2.label, ...WRAP }}>
                {toolLines.map((l, i) => <li key={i}>{l}</li>)}
              </Box>
            )}
          </Box>
        </Collapse>
      )}
      {footer}
    </Box>
  )
}

// A read-only reply with its placeholders highlighted (the INSERT tokens and
// the XXX ones, placeholderHighlight.js), as in the editor.
export const PLACEHOLDER_HIGHLIGHT_SX = { bgcolor: PLACEHOLDER_HIGHLIGHT_BG, color: PLACEHOLDER_HIGHLIGHT_TEXT, borderRadius: '3px', px: '4px', py: '1px', fontWeight: 600 }
export function PlaceholderHighlight({ text }) {
  return placeholderSegments(text).map((s, i) => (s.placeholder
    ? <Box key={i} component="span" data-testid="reply-placeholder" sx={PLACEHOLDER_HIGHLIGHT_SX}>{s.text}</Box>
    : s.text))
}
